/**
 * Style factory.
 * mkS(partner) returns a set of reusable inline style objects
 * pre-baked with the partner's brand colors.
 *
 * Usage:
 *   const s = mkS(activePartner);
 *   <button style={s.btn}>...</button>
 */
// v1.7.24.d — pick readable text (black or white) for a given background color, using WCAG
// relative luminance. Fixes light-branded personas (e.g. FoxSet #a5b4c9) where forcing white
// text on a pale brand color was unreadable. Solve once for ALL personas (Amenities Rule).
export function readableTextOn(hex) {
  if (!hex || typeof hex !== 'string') return '#fff';
  const m = hex.replace('#', '').match(/^([0-9a-f]{6}|[0-9a-f]{3})$/i);
  if (!m) return '#fff';
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const lin = (c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  const L = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
  // Luminance > ~0.5 = light background → use dark text; else white text.
  return L > 0.5 ? '#1a2133' : '#fff';
}

// Darken a hex color by a factor (0–1) — used so ghost text/borders stay readable on light brands.
export function darken(hex, factor = 0.35) {
  if (!hex || typeof hex !== 'string') return hex;
  const h = hex.replace('#', '');
  if (h.length !== 6) return hex;
  const c = [0, 2, 4].map((i) => Math.round(parseInt(h.slice(i, i + 2), 16) * (1 - factor)));
  return '#' + c.map((v) => v.toString(16).padStart(2, '0')).join('');
}

export function mkS(partner) {
  const primary = partner?.brand_color || '#0077cc';
  const secondary = partner?.secondary_color || primary;
  const onPrimary = readableTextOn(primary);
  // For ghost buttons on light brands, white text would be invisible — use a darkened brand tone.
  const ghostInk = readableTextOn(primary) === '#fff' ? primary : darken(primary, 0.45);

  return {
    // Solid primary button — text auto-contrasts to the brand color.
    btn: {
      background: primary,
      color: onPrimary,
      border: 'none',
      borderRadius: 6,
      padding: '8px 16px',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: 13,
    },
    // Ghost / outline button — ink darkened for light brands so it stays readable.
    btnGhost: {
      background: 'transparent',
      color: ghostInk,
      border: `1.5px solid ${ghostInk}`,
      borderRadius: 6,
      padding: '7px 15px',
      cursor: 'pointer',
      fontWeight: 600,
      fontSize: 13,
    },
    // Accent text
    accent: { color: primary },
    // Accent background chip/badge
    badge: {
      background: primary + '22',
      color: primary,
      borderRadius: 20,
      padding: '2px 10px',
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.04em',
    },
    // Active nav item highlight
    navActive: {
      background: primary + '22',
    },
    // Card header strip
    cardHeader: {
      background: `linear-gradient(135deg, ${primary}, ${secondary})`,
      color: '#fff',
      padding: '16px 20px',
      borderRadius: '8px 8px 0 0',
    },
    // Progress bar fill
    progressFill: {
      background: `linear-gradient(90deg, ${primary}, ${secondary})`,
      height: '100%',
      borderRadius: 4,
      transition: 'width 0.4s ease',
    },
    // Dotted left border for timeline / activity items
    timelineDot: {
      borderLeft: '2px solid var(--gov-slate-300)',
      paddingLeft: 12,
      marginLeft: 8,
    },
  };
}
